import React, { useRef, useState, useEffect } from "react";
import Text from "../../../../components/ui/Text";
import resetImage from "../../assets/images/reset.png";

export interface DateInputProps {
  /** Initial date value */
  value?: Date | null;
  /** Called when user picks a new date */
  onChange?: (date: Date | null) => void;
  /** Called when user clicks reset button */
  onReset?: () => void;
  /** Extra classes for wrapper */
  className?: string;
}

export const DateInput: React.FC<DateInputProps> = ({
  value: valueFromProps = null,
  onChange,
  onReset,
  className,
}) => {
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState<Date | null>(valueFromProps);

  // Sync with props if it changes
  useEffect(() => {
    setValue(valueFromProps ?? null);
  }, [valueFromProps]);

  // Convert Date → string for <input type="date">
  const formatForInput = (date: Date | null): string =>
    date ? date.toISOString().split("T")[0] : "";

  // Convert Date → DD.MM.YYYY (display only)
  const formatForDisplay = (date: Date | null): string | null => {
    if (!date) return null;
    return date.toLocaleDateString("de-DE"); // npr. 25.08.2025
  };

  // Handle <input> change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (!inputValue) {
      setValue(null);
      onChange?.(null);
      return;
    }
    const [year, month, day] = inputValue.split("-").map(Number);
    const newDate = new Date(year, month - 1, day);
    setValue(newDate);
    onChange?.(newDate);
  };

  // Open native date picker
  const openDatePicker = () => {
    dateInputRef.current?.showPicker?.(); // modern browsers
    dateInputRef.current?.click(); // fallback
  };

  return (
    <div
      className={`flex items-center justify-between rounded-md bg-[#161820]/70 px-3 py-2 ${className}`}
    >
      {/* Hidden Date Input */}
      <input
        ref={dateInputRef}
        type="date"
        value={formatForInput(value)}
        onChange={handleChange}
        className="hidden w-full"
      />

      {/* Display date text */}
      <Text size="xs" className="text-[#BEB9FF] font-sans text-center flex-1">
        {formatForDisplay(value) || (
          <span className="cursor-pointer" onClick={openDatePicker}>
            Pick a date
          </span>
        )}
      </Text>

      {/* Reset / Open button */}
      <button
        type="button"
        onClick={() => {
          setValue(null);
          onReset?.();
          openDatePicker();
        }}
        className="p-[1px] rounded-sm hover:opacity-80 bg-gradient-to-b from-[#d7eb51] to-[#646e34] transition"
      >
        <div className="bg-gradient-to-r from-[#43492B] to-[#41472B] p-1">
          <img src={resetImage} alt="res" className="w-[11px] h-[11px] " />
        </div>
      </button>
    </div>
  );
};
